import React, { useState, useEffect } from 'react';
import { X, Video, Phone, MessageSquare, User, Mail, Sparkles } from 'lucide-react';

const topics = [
  'Meditation & Focus',
  'Ayurvedic Wellness',
  'Yogic Philosophy',
  'Retreat Planning',
  'Emotional Healing'
];

export default function TalkToExpert() {
  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const [formData, setFormData] = useState({
    name: localStorage.getItem('user_name') || '',
    email: localStorage.getItem('user_email') || '',
    date: '',
    time: '',
    query: ''
  });

  const [consultationMode, setConsultationMode] = useState('video');
  const [selectedTopic, setSelectedTopic] = useState('Meditation & Focus');
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = showModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showModal]);

  const generateSlots = () => {
    const slots = [];
    for (let h = 9; h <= 17; h++) {
      for (let m = 0; m < 60; m += 30) {
        if (h === 17 && m > 0) continue;
        const hh = h.toString().padStart(2, '0');
        const mm = m.toString().padStart(2, '0');
        slots.push(`${hh}:${mm}`);
      }
    }
    return slots;
  };

  const allSlots = generateSlots();

  useEffect(() => {
    if (formData.date) {
      const fetchBookedSlots = async () => {
        setLoadingSlots(true);
        try {
          const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          const res = await fetch(`${apiBase}/api/scheduled-calls/booked-slots?date=${formData.date}`);
          if (res.ok) {
            const data = await res.json();
            setBookedSlots(data);
            if (formData.time && data.includes(formData.time)) {
              setFormData(prev => ({ ...prev, time: '' }));
            }
          }
        } catch (e) {
          console.error('Failed to fetch booked slots');
        } finally {
          setLoadingSlots(false);
        }
      };
      fetchBookedSlots();
    } else {
      setBookedSlots([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.date]);

  const availableSlots = allSlots.filter(slot => !bookedSlots.includes(slot));

  const handleScheduleClick = (e) => {
    e.stopPropagation();
    setShowModal(true);
    setIsHovered(false);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!formData.time) { showToast('Please select a time slot.', 'error'); return; }
    setLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiBase}/api/meet/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_name: formData.name || 'Seeker',
          user_email: formData.email,
          date: formData.date,
          time: formData.time,
          query: `[Mode: ${consultationMode.toUpperCase()}] [Topic: ${selectedTopic}] ${formData.query}`
        })
      });
      if (response.ok) {
        showToast('Call scheduled! Our expert will contact you soon.', 'success');
        setShowModal(false);
        setFormData({ ...formData, date: '', time: '', query: '' });
      } else {
        showToast('Could not schedule. Please try again.', 'error');
      }
    } catch {
      showToast('Network error. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '9px 10px 9px 30px', borderRadius: '8px', border: '1px solid rgba(181,137,37,0.2)', background: '#fefefe', color: 'var(--text-light)', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.65rem', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: '700' };

  return (
    <>
      {/* FLOATING BUTTON */}
      <div className="talk-to-expert">
        <div
          onClick={handleScheduleClick}
          className="expert-btn animate-pulse-shadow floating-widget floating-widget-right"
          style={{
            position: 'fixed', bottom: '100px', right: '24px', zIndex: 9000,
            background: 'rgba(250, 247, 242, 0.98)', backdropFilter: 'blur(12px)',
            border: '1px solid rgba(181, 137, 37, 0.3)', borderRadius: '30px',
            padding: '8px 18px 8px 8px', display: 'flex', alignItems: 'center', gap: '12px',
            boxShadow: '0 10px 30px rgba(41, 33, 26, 0.15)', animation: 'fadeIn 1s ease-out 0.5s both',
            cursor: loading ? 'wait' : 'pointer', transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
            opacity: loading ? 0.7 : 1, transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="expert-icon-wrap" style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(181,137,37,0.1)', border: '1px solid rgba(181,137,37,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary-gold,#b58925)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
          </div>
          <div className="expert-text-container" style={{ textAlign: 'left' }}>
            <div className="expert-text-title" style={{ color: 'var(--primary-gold,#b58925)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 800, marginBottom: '2px' }}>
              {loading ? 'Connecting...' : 'Consult Guide'}
            </div>
            <div className="expert-text-sub" style={{ color: 'var(--text-muted,#706152)', fontSize: '0.75rem', letterSpacing: '0.02em', fontWeight: 500 }}>
              {loading ? 'Please wait' : 'Free Consultation'}
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div
          className="consult-overlay"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(10,8,6,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)', overflow: 'auto', padding: '20px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="animate-slide-up consult-modal" style={{ background: 'linear-gradient(145deg,#ffffff 0%,#faf7f2 100%)', borderRadius: '24px', width: '100%', maxWidth: '960px', position: 'relative', border: '1px solid rgba(181,137,37,0.25)', boxShadow: '0 30px 60px rgba(0,0,0,0.35)', padding: 'var(--modal-pad, 36px 40px)' }}>

            {/* Close */}
            <button onClick={() => setShowModal(false)}
              style={{ position: 'absolute', top: '18px', right: '18px', background: 'rgba(255,255,255,0.9)', border: '1px solid rgba(181,137,37,0.2)', borderRadius: '50%', width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)', cursor: 'pointer', zIndex: 10, transition: 'all 0.2s ease' }}
              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            ><X size={16} /></button>

            {/* Header */}
            <div style={{ marginBottom: '22px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'rgba(181,137,37,0.1)', border: '1px solid rgba(181,137,37,0.2)', borderRadius: '20px', padding: '3px 12px', marginBottom: '10px' }}>
                <Sparkles size={11} style={{ color: 'var(--primary-gold)' }} />
                <span style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--primary-gold)', fontWeight: '700' }}>Free Consultation</span>
              </div>
              <h2 className="font-serif" style={{ fontSize: '1.55rem', marginBottom: '4px', color: 'var(--text-light)', lineHeight: '1.2' }}>Begin Your Spiritual Consultation</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Choose your preferences and our expert will reach out at your selected time.</p>
            </div>

            {/* 2-Column Grid Form */}
            <form onSubmit={handleModalSubmit}>
              <div className="consult-form-grid" style={{ display: 'grid', gridTemplateColumns: 'var(--grid-cols, 1fr 1fr)', gap: 'var(--grid-gap, 28px)' }}>

                {/* LEFT */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

                  {/* Name + Email */}
                  <div className="consult-name-email" style={{ display: 'flex', gap: '12px' }}>
                    <div className="consult-field" style={{ flex: 1 }}>
                      <label style={labelStyle}>Your Name</label>
                      <div style={{ position: 'relative' }}>
                        <User size={12} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary-gold)', opacity: 0.8 }} />
                        <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Arjun Sharma" style={inputStyle} />
                      </div>
                    </div>
                    <div className="consult-field" style={{ flex: 1 }}>
                      <label style={labelStyle}>Email Address</label>
                      <div style={{ position: 'relative' }}>
                        <Mail size={12} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary-gold)', opacity: 0.8 }} />
                        <input type="email" required value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="you@email.com" style={inputStyle} />
                      </div>
                    </div>
                  </div>

                  {/* Mode */}
                  <div>
                    <label style={labelStyle}>Communication Mode</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[{ id: 'video', label: 'Video Meet', icon: Video }, { id: 'audio', label: 'Voice Call', icon: Phone }, { id: 'chat', label: 'WhatsApp', icon: MessageSquare }].map(mode => {
                        const Icon = mode.icon; const active = consultationMode === mode.id;
                        return (
                          <div key={mode.id} onClick={() => setConsultationMode(mode.id)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '9px 6px', borderRadius: '8px', cursor: 'pointer', border: active ? '1px solid var(--primary-gold)' : '1px solid rgba(181,137,37,0.15)', background: active ? 'rgba(181,137,37,0.1)' : '#fefefe', color: active ? 'var(--primary-gold)' : 'var(--text-muted)', fontSize: '0.75rem', fontWeight: '600', transition: 'all 0.25s ease', boxShadow: active ? '0 2px 8px rgba(181,137,37,0.12)' : 'none' }}>
                            <Icon size={13} />{mode.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Query */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>What brings you to seek guidance?</label>
                    <textarea required value={formData.query} onChange={e => setFormData({ ...formData, query: e.target.value })} placeholder="Share a little about what brings you here..." style={{ flex: 1, minHeight: '120px', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(181,137,37,0.2)', background: '#fefefe', color: 'var(--text-light)', fontSize: '0.82rem', outline: 'none', resize: 'none', boxSizing: 'border-box', lineHeight: '1.6' }} />
                  </div>
                </div>

                {/* RIGHT */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

                  {/* Focus chips */}
                  <div>
                    <label style={labelStyle}>Session Focus</label>
                    <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                      {topics.map(t => {
                        const active = selectedTopic === t;
                        return (
                          <div key={t} onClick={() => setSelectedTopic(t)} style={{ padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', border: active ? '1px solid var(--primary-gold)' : '1px solid rgba(181,137,37,0.15)', background: active ? 'var(--primary-gold)' : '#fefefe', color: active ? '#fff' : 'var(--text-muted)', fontSize: '0.72rem', fontWeight: '600', transition: 'all 0.2s ease', boxShadow: active ? '0 2px 8px rgba(181,137,37,0.25)' : 'none' }}>{t}</div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Date */}
                  <div>
                    <label style={labelStyle}>Preferred Date</label>
                    <input type="date" required value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(181,137,37,0.2)', background: '#fefefe', color: 'var(--text-light)', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }} />
                  </div>

                  {/* Slot grid */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <label style={labelStyle}>
                      Available Time Slots
                      {formData.time && <span style={{ marginLeft: '6px', color: 'var(--primary-gold)', textTransform: 'none', letterSpacing: 0, fontWeight: '600' }}>— {formData.time} selected</span>}
                    </label>
                    {!formData.date ? (
                      <div style={{ padding: '14px', borderRadius: '8px', border: '1px dashed rgba(181,137,37,0.3)', color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'center', background: 'rgba(0,0,0,0.015)', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Please select a date first</div>
                    ) : loadingSlots ? (
                      <div style={{ fontSize: '0.78rem', color: 'var(--primary-gold)', padding: '8px 0' }}>Loading slots...</div>
                    ) : availableSlots.length === 0 ? (
                      <div style={{ fontSize: '0.78rem', color: '#c0392b', padding: '8px 0' }}>No slots available for this day.</div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(68px, 1fr))', gap: '7px' }}>
                        {availableSlots.map(slot => {
                          const active = formData.time === slot;
                          return (
                            <div key={slot} onClick={() => setFormData({ ...formData, time: slot })} style={{ padding: '8px 4px', textAlign: 'center', borderRadius: '7px', cursor: 'pointer', border: active ? '1px solid var(--primary-gold)' : '1px solid rgba(181,137,37,0.15)', background: active ? 'var(--primary-gold)' : '#fefefe', color: active ? '#fff' : 'var(--text-light)', fontSize: '0.75rem', fontWeight: '600', transition: 'all 0.2s ease', boxShadow: active ? '0 2px 8px rgba(181,137,37,0.25)' : 'none' }}>{slot}</div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Submit */}
                  <button type="submit" disabled={loading}
                    style={{ width: '100%', padding: '13px', opacity: loading ? 0.7 : 1, cursor: loading ? 'wait' : 'pointer', display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,var(--primary-gold) 0%,#b89225 100%)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '0.88rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', boxShadow: '0 6px 20px rgba(181,137,37,0.35)', transition: 'all 0.3s ease' }}
                    onMouseOver={e => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <Sparkles size={15} />
                    {loading ? 'Confirming...' : 'Schedule Consultation'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div className="consult-toast" style={{ position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', background: toast.type === 'success' ? '#2ecc71' : '#e74c3c', color: 'white', padding: '12px 24px', borderRadius: '30px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', zIndex: 10000, fontWeight: '500', fontSize: '0.95rem', animation: 'fadeIn 0.3s ease-out', display: 'flex', alignItems: 'center', gap: '10px' }}>
          {toast.type === 'success'
            ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>
          }
          {toast.message}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .consult-overlay { align-items: flex-start !important; padding: 60px 16px !important; }
          .expert-text-container { display: none !important; }
          .expert-btn { padding: 0 !important; border-radius: 50% !important; width: 56px !important; height: 56px !important; bottom: 94px !important; }
          .expert-icon-wrap { width: 56px !important; height: 56px !important; }

          .consult-modal { --modal-pad: 20px 16px !important; }
          .consult-form-grid { --grid-cols: 1fr !important; --grid-gap: 16px !important; }
          .consult-name-email { flex-direction: column !important; }
          .consult-field { flex: none !important; width: 100% !important; }

          .consult-modal { border-radius: 16px !important; }
          .consult-modal h2 { font-size: 1.2rem !important; }
          .consult-modal p { font-size: 0.78rem !important; }
          .consult-modal textarea { min-height: 80px !important; }
        }
        @media (max-width: 480px) {
          .consult-modal { --modal-pad: 16px 12px !important; }
          .consult-form-grid { --grid-gap: 12px !important; }
        }
        input:focus, select:focus, textarea:focus {
          border-color: var(--primary-gold) !important;
          box-shadow: 0 0 0 3px rgba(181,137,37,0.15) !important;
          background: #ffffff !important;
        }
      `}</style>
    </>
  );
}
